import _ from 'lodash';

export function parseLocalDate(dateStr) {
    if (!dateStr) return null;
    if (dateStr instanceof Date) {
        return isNaN(dateStr.getTime()) ? null : dateStr;
    }
    if (typeof dateStr === 'string' && dateStr !== '') {
        const parts = dateStr.split('T')[0].split('-');
        if (parts.length === 3) {
            const y = Number(parts[0]);
            const m = Number(parts[1]) - 1;
            const d = Number(parts[2]);
            if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
                return new Date(y, m, d);
            }
        }
        const parsed = new Date(dateStr);
        return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
}

export function formatLocalDate(dateObj) {
    if (!dateObj) return '';
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const d = String(dateObj.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

export function isDateWeekend(ymd) {
    try {
        const parsed = parseLocalDate(ymd);
        if (!parsed) return false;
        const day = parsed.getDay();
        return day === 0 || day === 6;
    } catch (e) {
        return false;
    }
}

export function isDateHoliday(ymd, publicHolidays) {
    if (!publicHolidays) return false;
    if (typeof publicHolidays === 'function') {
        try {
            return publicHolidays(ymd);
        } catch (e) {
            return false;
        }
    }
    if (Array.isArray(publicHolidays)) {
        return publicHolidays.some(h => (typeof h === 'string' ? h === ymd : (h.date || h.dateUtc) === ymd));
    }
    if (typeof publicHolidays === 'object') {
        const year = ymd.substring(0, 4);
        const list = publicHolidays[year];
        if (Array.isArray(list)) {
            return list.some(h => (typeof h === 'string' ? h === ymd : (h.date || h.dateUtc) === ymd));
        }
    }
    return false;
}

export function getDayExistingHours(ymd, dayStatuses, capacity = 8) {
    const dayStat = _.find(dayStatuses || [], p => p && String(p.date || '').split('T')[0] === ymd);
    if (dayStat) {
        if (dayStat.style === 'absence' || dayStat.status === 'absence' || dayStat.isAbsence) {
            return -1; // Special indicator for booked absence
        }
        if (typeof dayStat.hours === 'number' && !Number.isNaN(dayStat.hours)) {
            return dayStat.hours;
        }
        if (typeof dayStat.totalHours === 'number' && !Number.isNaN(dayStat.totalHours)) {
            return dayStat.totalHours;
        }
        const parsedHours = parseFloat(dayStat.hours ?? dayStat.totalHours);
        if (!Number.isNaN(parsedHours)) {
            return parsedHours;
        }
        if (dayStat.style === 'done' || dayStat.status === 'completed') {
            return capacity;
        }
        if (dayStat.style === 'pending' || dayStat.status === 'incomplete') {
            return Math.round(capacity * 0.5 * 100) / 100;
        }
    }
    return 0;
}

export function getDayGap(ymd, dayStatuses, capacity = 8, publicHolidays = null) {
    if (isDateWeekend(ymd)) return 0;
    if (isDateHoliday(ymd, publicHolidays)) return 0;

    const logged = getDayExistingHours(ymd, dayStatuses, capacity);
    if (logged === -1) return 0; // Absence day
    if (logged >= capacity) return 0;

    const gap = capacity - logged;
    return Math.round(Math.max(0, gap) * 100) / 100;
}

/**
 * Calculates capacity-aware multi-date allocations across available gaps.
 */
export function autoFillGaps({
    deltaHours = 0,
    dailyCapacity = 8,
    calendarDayStatuses = [],
    publicHolidays = null,
    minDate = null,
    maxDate = null,
    suggestedDate = null,
    allowOvertime = false
}) {
    const totalTarget = Math.round((parseFloat(deltaHours) || 0) * 100) / 100;
    const capacity = dailyCapacity > 0 ? dailyCapacity : 8;

    if (totalTarget <= 0) {
        return {
            allocations: [],
            totalAllocated: 0,
            remainingHours: 0,
            totalGapsFound: 0,
            isComplete: true
        };
    }

    const todayYMD = formatLocalDate(new Date());
    const effectiveMax = maxDate || todayYMD;

    let effectiveMin = minDate;
    if (!effectiveMin) {
        const d = parseLocalDate(effectiveMax) || new Date();
        const past = new Date(d);
        past.setDate(past.getDate() - 30);
        effectiveMin = formatLocalDate(past);
    }

    // Generate list of all calendar dates between effectiveMin and effectiveMax
    const datesList = [];
    const minParsed = parseLocalDate(effectiveMin) || new Date();
    const maxParsed = parseLocalDate(effectiveMax) || new Date();

    const curr = new Date(minParsed);
    while (curr <= maxParsed) {
        datesList.push(formatLocalDate(curr));
        curr.setDate(curr.getDate() + 1);
    }

    // Calculate gap for each candidate date
    const candidates = [];
    let totalGapsFound = 0;

    for (const d of datesList) {
        const gap = getDayGap(d, calendarDayStatuses, capacity, publicHolidays);
        const existing = getDayExistingHours(d, calendarDayStatuses, capacity);
        candidates.push({ date: d, gap, existing: existing === -1 ? 0 : existing, isAbsence: existing === -1 });
        totalGapsFound += gap;
    }

    totalGapsFound = Math.round(totalGapsFound * 100) / 100;

    // Prioritize walking backwards from suggestedDate down to minDate
    const anchor = suggestedDate && suggestedDate <= effectiveMax && suggestedDate >= effectiveMin
        ? suggestedDate
        : effectiveMax;

    // Split candidates into those <= anchor (reverse ordered) and those > anchor (reverse ordered)
    const beforeAnchor = candidates.filter(c => c.date <= anchor).reverse();
    const afterAnchor = candidates.filter(c => c.date > anchor).reverse();
    const prioritized = [...beforeAnchor, ...afterAnchor];

    let remaining = totalTarget;
    const allocationsMap = {};

    for (const cand of prioritized) {
        if (remaining <= 0.001) break;
        if (cand.gap > 0) {
            const alloc = Math.round(Math.min(remaining, cand.gap) * 100) / 100;
            if (alloc > 0) {
                allocationsMap[cand.date] = alloc;
                remaining = Math.round((remaining - alloc) * 100) / 100;
            }
        }
    }

    // If remaining hours still exist and allowOvertime is enabled, distribute over non-absence workdays
    if (remaining > 0.001 && allowOvertime) {
        const eligibleWorkdays = prioritized.filter(c => !isDateWeekend(c.date) && !candIsHoliday(c.date, publicHolidays) && !c.isAbsence);
        for (const cand of eligibleWorkdays) {
            if (remaining <= 0.001) break;
            const currentAlloc = allocationsMap[cand.date] || 0;
            const currentTotal = cand.existing + currentAlloc;
            const maxAllowedOvertime = capacity * 1.5; // up to 12h max daily
            const roomForOvertime = Math.max(0, maxAllowedOvertime - currentTotal);
            if (roomForOvertime > 0) {
                const add = Math.round(Math.min(remaining, roomForOvertime) * 100) / 100;
                allocationsMap[cand.date] = Math.round((currentAlloc + add) * 100) / 100;
                remaining = Math.round((remaining - add) * 100) / 100;
            }
        }
    }

    // Convert map to sorted list
    const allocations = Object.keys(allocationsMap)
        .sort()
        .map(date => ({
            date,
            hours: allocationsMap[date]
        }));

    const totalAllocated = Math.round(allocations.reduce((sum, a) => sum + a.hours, 0) * 100) / 100;
    const finalRemaining = Math.round(Math.max(0, totalTarget - totalAllocated) * 100) / 100;

    return {
        allocations,
        totalAllocated,
        remainingHours: finalRemaining,
        totalGapsFound,
        isComplete: finalRemaining <= 0.001
    };
}

function candIsHoliday(ymd, publicHolidays) {
    return isDateHoliday(ymd, publicHolidays);
}
